<template>
  <div :class="$options.name">
    <form v-if="!sent"
      class="contact-form"
      novalidate
      @submit.prevent="submit">

      <!-- Products list -->
      <div v-if="products.length"
        class="contact-form__products">
        <h4>Valitud tooted</h4>
        <div v-for="p in products"
          :key="p.key || p.id"
          class="contact-form__product">
          <div class="contact-form__product-info">
            <span>{{ p.name }}</span>
            <span v-if="p.variation"
              class="contact-form__product-variation">{{ p.variation }}</span>
          </div>
          <span class="contact-form__product-price">{{ p.price }} €</span>
          <button type="button"
            class="contact-form__remove"
            @click="$emit('remove-product', p.key || p.id)">
            <i class="fa-solid fa-xmark" />
          </button>
        </div>
      </div>

      <Field v-model="form.name"
        size="lg"
        icon="fa-regular fa-user"
        autocomplete="name"
        placeholder="Sinu nimi"
        :error="shownErrors.name" />
      <Field v-model="form.email"
        type="email"
        size="lg"
        placeholder="Sinu e-post"
        :error="shownErrors.email" />
      <Field v-model="form.phone"
        type="tel"
        size="lg"
        placeholder="Sinu telefon"
        :error="shownErrors.phone"
        @input="form.phone = form.phone.replace(/[^0-9+]/g, '')" />
      <!-- A product request is about the products; the service choice is for the general form. -->
      <Field v-if="!products.length"
        v-model="form.service"
        type="select"
        size="lg"
        icon="fa-solid fa-screwdriver-wrench"
        placeholder="Vali teenus"
        :options="serviceOptions"
        label-key="label"
        value-key="value"
        :clearable="false"
        :loading="loadingServices" />
      <Field v-model="form.message"
        type="textarea"
        size="lg"
        placeholder="Sinu sõnum"
        :rows="4"
        autogrow
        :error="shownErrors.message" />
      <btn type="submit"
        icon="fa-solid fa-paper-plane"
        :disabled="sending">{{ sending ? 'Saadan...' : 'Saada sõnum' }}</btn>
    </form>
    <div v-else
      class="contact-form__success">
      <i class="fa-solid fa-circle-check" />
      <h3>Sõnum saadetud!</h3>
      <p>Täname! Vastame esimesel võimalusel.</p>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import { ServiceModel } from '@/models/ServiceModel'

// Mirrors aktuaalne-api's util/visitor-input.ts; the server has the final say.
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const PHONE = /^\+?[0-9]{7,15}$/

export default {
  name: 'contact-form',
  emits: ['remove-product'],
  props: {
    selectedService: { type: [String, Number], default: '' },
    products: { type: Array, default: () => [] },
  },
  data() {
    return {
      sending: false,
      sent: false,
      shownErrors: {},
      form: {
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      },
      serviceOptions: [],
      loadingServices: false,
    }
  },
  computed: {
    errors() {
      const name = this.form.name.trim()
      const email = this.form.email.trim()
      const message = this.form.message.trim()
      const messageRequired = !this.products.length
      return {
        name: !name ? 'Sisesta oma nimi'
          : name.length < 2 ? 'Nimi on liiga lühike'
          : name.length > 100 ? 'Nimi on liiga pikk' : '',
        email: !email ? 'Sisesta oma e-post'
          : !EMAIL.test(email) || email.length > 254 ? 'E-posti aadress ei ole korrektne' : '',
        phone: this.form.phone && !PHONE.test(this.form.phone) ? 'Telefoninumber ei ole korrektne' : '',
        message: messageRequired && !message ? 'Kirjuta oma sõnum'
          : messageRequired && message.length < 10 ? 'Sõnum on liiga lühike (vähemalt 10 tähemärki)'
          : message.length > 5000 ? 'Sõnum on liiga pikk (kuni 5000 tähemärki)' : '',
      }
    },
  },
  created() {
    if (!this.products.length) this.fetchServices()
  },
  watch: {
    selectedService(val) {
      this.form.service = val
    }
  },
  methods: {
    async fetchServices() {
      this.loadingServices = true
      try {
        const result = await new ServiceModel().find({ asPlainData: true })
        const items = Array.isArray(result) ? result : result.data || []
        this.serviceOptions = [
          ...items
            .filter(s => s.isActive !== false)
            .map(s => ({ value: s.id, label: s.title })),
          { value: 'muu', label: 'Muu' },
        ]
        if (this.selectedService) {
          this.form.service = this.selectedService
        }
      } catch (e) {
        console.warn('Failed to load service options', e)
      } finally {
        this.loadingServices = false
      }
    },
    async submit() {
      // A snapshot: the messages change only on the next send, not while typing.
      this.shownErrors = { ...this.errors }
      if (Object.values(this.shownErrors).some(Boolean)) return
      this.sending = true
      try {
        if (this.products.length) {
          const payload = {
            name: this.form.name,
            email: this.form.email,
            phone: this.form.phone,
            message: this.form.message,
            products: this.products,
          }
          await api.post('/product-requests', payload)
        } else {
          await api.post('/contacts', { ...this.form })
        }
        this.sent = true
      } catch (e) {
        const reason = e?.response?.status === 400 && typeof e.response.data === 'string' ? e.response.data : ''
        this.$error(reason || 'Sõnumi saatmine ebaõnnestus. Proovi uuesti.')
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contact-form {
  display: flex;
  flex-direction: column;
  gap: 16rem;
}

.contact-form > .btn,
.contact-form > button[type="submit"] {
  align-self: center;
}

.contact-form__success {
  text-align: center;
  padding: 32rem 16rem;
}

.contact-form__success i {
  font-size: 48rem;
  color: var(--accent);
  margin-bottom: 12rem;
}

.contact-form__success h3 {
  margin-bottom: 8rem;
}

.contact-form__products {
  display: flex;
  flex-direction: column;
  gap: 8rem;

  h4 {
    font-size: 14rem;
    font-weight: 600;
    margin-bottom: 4rem;
  }
}

.contact-form__product {
  display: flex;
  align-items: center;
  gap: 8rem;
  padding: 8rem 12rem;
  background: var(--section-alt-bg, #f5f5f5);
  border-radius: 6rem;
  font-size: 14rem;
}

.contact-form__product-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.contact-form__product-variation {
  font-size: 12rem;
  color: #888;
}

.contact-form__product-price {
  font-weight: 600;
  white-space: nowrap;
}

.contact-form__remove {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--danger, #e74c3c);
  font-size: 14rem;
  padding: 2rem 4rem;
}
</style>
